<i18n>
{
  "fr": {
    "fast-rewind": "Au début",
    "goto-previous-timestamp": "Précédent",
    "goto-next-timestamp": "Suivant",
    "fast-forward": "A la fin"
  },
  "de": {

  }
}
</i18n>

<template>
  <div class="timeframe-tool">
    <div class="timeframe-events" v-if="show && showTimeline">
      <div v-if="mode === 'window'" class="timeserie-header window-mode">
        <span
          class="label"
          v-for="timeline in timelines" :key="timeline[0].timelineId"
        >
          {{ timeline[0].timelineName }}
        </span>
      </div>
      <div class="timeframe">
        <TimeframeSelector @mouseover="onMouseOver" :enableZoom="enableZoom" ref="timeframeSelector"/>
      </div>
    </div>
    <div v-if="mode === 'window'" class="datetime-selector window-mode">
      <div class="time-info">
        <span class="date-from">
          <!-- HACK: la timezone est "écrasée" dans la timeline, les données sont notés UTC mais comporte déjà le changement userUtcOffset -->
          {{ dateFrom | formatDatetime(0) }}
        </span>

        <span class="date-to">
          <!-- HACK: la timezone est "écrasée" dans la timeline, les données sont notés UTC mais comporte déjà le changement userUtcOffset -->
          {{ dateTo | formatDatetime(0) }}
        </span>
      </div>
    </div>
    <div v-if="mode === 'discrete'" class="datetime-selected discrete-mode" @wheel="mouseWheelOnDiscreteSelector">
      <div class="action">
        <span @click="fastRewind()" v-if="canFastRewind">
          <md-icon>first_page</md-icon>
          <md-tooltip>{{ $t('fast-rewind') }}</md-tooltip>
        </span>
        <span @click="gotoPreviousTimestamp()" v-if="hasPreviousTimestamp">
          <md-icon>chevron_left</md-icon>
          <md-tooltip>{{ $t('goto-previous-timestamp') }}</md-tooltip>
        </span>
      </div>
      <div class="middle-date">
        <span v-if="!showMonthOnly">{{ dateMiddle | formatMonthYear(0) }}</span>
        <span v-if="showMonthOnly">{{ dateMiddle | formatMonth(0) }}</span>
      </div>
      <div class="action">
        <span @click="gotoNextTimestamp()" v-if="hasNextTimestamp">
          <md-icon>chevron_right</md-icon>
          <md-tooltip>{{ $t('goto-next-timestamp') }}</md-tooltip>
        </span>
        <span @click="fastForward()" v-if="canFastForward">
          <md-icon>last_page</md-icon>
          <md-tooltip>{{ $t('fast-forward') }}</md-tooltip>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

import TimeframeSelector from '@/components/timeframe/selector'
import TimeStoreMixin from '@/mixins/store/time'
import LayerStoreMixin from '@/mixins/store/layer'

export default {
  name: 'TimeframeTool',
  mixins: [
    TimeStoreMixin,
    LayerStoreMixin
  ],
  components: {
    TimeframeSelector
  },
  props: {
    mode: {
      type: String,
      default: 'window'
    },
    // La sélection se fait au millieu de la timeline
    halfWindowSize: {
      type: Object,
      default: () => { return moment.duration(1, 'year')}
    },
    showTimeline: {
      type: Boolean,
      default: true
    },
    enableZoom: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    show () {
      return this.events.length > 0
    },
    hasNextTimestamp () {
      return this.dateMiddle.isBefore(this.lastValueDate)
    },
    hasPreviousTimestamp () {
      return this.dateMiddle.isAfter(this.firstValueDate)
    },
    canFastForward () {
      return this.hasNextTimestamp
    },
    canFastRewind () {
      return this.hasPreviousTimestamp
    },
    showMonthOnly () {
      // Parmis les layers temporels, si tous demande de n'afficher que le mois, alors on n'affiche que le mois
      return !!this.temporalLayers.map((layer) => layer.geoserverConfig.showMonthOnly).reduce((prev, curr) => prev && curr, true)
    }
  },
  methods: {
    onMouseOver (timestamp) {
      this.$emit('mouseover', timestamp)
    },
    moveTo (domain) {
      if (typeof this.$refs.timeframeSelector !== 'undefined') return this.$refs.timeframeSelector.moveTo(domain)
      this.setTimeframe(domain)
    },
    gotoPreviousTimestamp () {
      let event = this.dateMiddle.subtract(1, 'month')
      let dateFrom = event.clone()
      let dateTo = event.clone()

      dateFrom.subtract(this.halfWindowSize)
      dateTo.add(this.halfWindowSize)

      this.moveTo([dateFrom, dateTo])
    },
    gotoNextTimestamp () {
      let event = this.dateMiddle.add(1, 'month')
      let dateFrom = event.clone()
      let dateTo = event.clone()

      dateFrom.subtract(this.halfWindowSize)
      dateTo.add(this.halfWindowSize)

      this.moveTo([dateFrom, dateTo])
    },
    fastForward () {
      let event = this.dateMiddle.add(1, 'year')
      if (event.isAfter(this.lastValueDate)) event = this.lastValueDate.clone()
      let dateFrom = event.clone()
      let dateTo = event.clone()

      dateFrom.subtract(this.halfWindowSize)
      dateTo.add(this.halfWindowSize)

      this.moveTo([dateFrom, dateTo])
    },
    fastRewind () {
      let event = this.dateMiddle.subtract(1, 'year')
      if (event.isBefore(this.firstValueDate)) event = this.firstValueDate.clone()
      let dateFrom = event.clone()
      let dateTo = event.clone()

      dateFrom.subtract(this.halfWindowSize)
      dateTo.add(this.halfWindowSize)

      this.moveTo([dateFrom, dateTo])
    },
    mouseWheelOnDiscreteSelector (event) {
      if (event.deltaY < 0 && this.hasNextTimestamp) return this.gotoNextTimestamp()
      if (event.deltaY > 0 && this.hasPreviousTimestamp) return this.gotoPreviousTimestamp()
    }
  }
}
</script>


<style lang="scss" scoped>
  .timeframe-events {
    display: flex;
    padding: 15px;
    padding-bottom: 0px;
  }

  .timeframe {
    border-left: 1px solid black;
    border-right: 1px solid black;
    padding-left: 10px;
    padding-right: 10px;
    overflow: hidden;
    width: 100%;
    cursor: grab;
  }

  .timeserie-header {
    margin-top: -6px;
    margin-right: 20px;
    width: 185px;

    .label {
      display: block;
      font-size: 12px;
      height: 12px;
      margin-bottom: 3px;
      cursor: pointer;
      text-align: right;
      margin-top: 1px;
    }
  }

  .datetime-selector {
    z-index: 20;

    .time-info {
      display: flex;
      padding-left: 197px;
      padding-right: 15px;
      justify-content: space-between;
      align-items: flex-start;

      span {
        user-select: none;
      }
    }

    .date-from {
      background: #184aa0;
      color: white;
      padding: 5px;
      padding-top: 2px;
      padding-bottom: 2px;
      border-radius: 0px 5px 5px 5px;
    }

    .date-to {
      background: #184aa0;
      color: white;
      padding: 5px;
      padding-top: 2px;
      padding-bottom: 2px;
      border-radius: 5px 0px 5px 5px;
    }

    .aggregation-mode {
      width: 200px;
      margin-bottom: 0px;
    }
  }

  .datetime-selected {
    display: flex;
    justify-content: space-around;
    user-select: none;

    .action span {
      cursor: pointer;
    }

    &.discrete-mode {
      .middle-date {
        text-align: center;
        min-width: 110px;
      }
      .action {
        min-width: 48px;
      }
    }
  }
</style>